import type { MonthlyTableCashFlowMonth } from 'features/drilldown/cashflows/gql/__generated__/MonthlyTableCashFlowMonth';
import type { GetDealRemittanceSummaries_getDealRemittanceSummaries_summaries_by_payment_month } from './__generated__/GetDealRemittanceSummaries';
import { toNum } from './utils';

/**
 * Maps getDealRemittanceSummaries response (summaries by payment month) to the shape
 * expected by the cashflow Chart and MonthlyTable. Uses ordinal month (1..N) for x-axis.
 *
 * Pool-level (100% tape) view — chart + MonthlyTable show the traded pool's
 * cashflows, which both buyer and seller see the same way. Per-party decomposition
 * lives in the sidecar (`RemittanceMetrics`), which reads full-tape totals directly
 * off the summary response and splits by `transferPercent`.
 *
 * Field mapping:
 *  - principal/interest/payments: `*_full_cents` (full-tape totals).
 *  - balances + prep/def/loss: unsuffixed `total_*_cents` / `*_balance_cents`.
 *  - servicing: `total_fees_cents` (full-tape; informational on the chart, goes
 *    entirely to the servicer).
 *
 * `cash_flow_cents` is reconstructed as principal + interest so the MonthlyTable's
 * cashflow column reflects cash collected in the month, net of the implicit
 * servicing outflow.
 */
export function summariesByPaymentMonthToMonthlyTableFull(
  summariesByPaymentMonth: GetDealRemittanceSummaries_getDealRemittanceSummaries_summaries_by_payment_month[],
): MonthlyTableCashFlowMonth[] {
  return summariesByPaymentMonth.map((entry, index) => {
    const s = entry.summary;
    const principal = toNum(s?.total_principal_full_cents);
    const interest = toNum(s?.total_interest_full_cents);
    const payments = toNum(s?.total_payments_full_cents);
    const prepayments = toNum(s?.total_prepayments_cents);
    const defaults = toNum(s?.total_defaults_cents);
    const losses = toNum(s?.total_losses_cents);
    const servicing = toNum(s?.total_fees_cents);
    const starting = toNum(s?.starting_balance_cents);
    const ending = toNum(s?.ending_balance_cents);
    // Cash-collected proxy matching the investor mapper's convention (principal +
    // interest). On the full-tape chart this represents total pool cash inflows;
    // the sidecar computes Investor/Servicer shares separately.
    const cashFlowCents = principal + interest;
    return {
      month: index + 1,
      starting_balance_cents: starting,
      payments_cents: payments,
      interest_cents: interest,
      principal_cents: principal,
      servicing_cents: servicing,
      prepayments_cents: prepayments,
      defaults_cents: defaults,
      losses_cents: losses,
      ending_balance_cents: ending,
      cash_flow_cents: cashFlowCents,
    };
  });
}
